<template>
  <div class="filtersSlider">
    <h5 class="filtersSlider__title">
      <span>{{ title }}</span>
    </h5>
    <div class="filtersSlider__value">
      <span>{{ sliderHeaderText }}</span>
    </div>
    <div class="sliderWrapper sliderWrapper--filter">
      <BaseSlider
        v-model:modelValue="sliderValues"
        :config="sliderConfig"
        @update="sliderUpdateEvent"
      />
    </div>
  </div>
  <!-- Buttons -->
  <FiltersItemTimeSliderButtons
    :unique-times="uniqueTimes"
    :current-indexes="sliderValues"
    @update:filterObject="handleButtonClick"
  />
</template>
<script setup>
import { useFiltersStore } from './store/filtersStore';
import { useResultQueryStore } from './store/resultQueryStore';

const filtersStore = useFiltersStore();
const resultQueryStore = useResultQueryStore();

import { addZero } from '@js/utils/toolbox';
import { dateFormat } from '@js/utils/dateFns';

const props = defineProps({
	title: {
		type: String,
		required: true
	},
	propertyKey: {
		type: String,
		required: true
	},
	tripIndex: {
		type: Number,
		required: true
	},
	uniqueTimes: {
		type: Array,
		required: true
	},
	type: {
		type: String,
		required: true
	}
});

const sliderHeaderText = ref('');
const filter = ref({
	tripIndex: props.tripIndex,
	start: props.uniqueTimes[0],
	end: props.uniqueTimes[props.uniqueTimes.length - 1]
});

const sliderConfig = computed(() => ({
	connect: [false, true, false],
	tooltips: [{ to: tooltipFormatter }, { to: tooltipFormatter }],
	range: {
		min: [0],
		max: [props.uniqueTimes.length - 1]
	},
	handleAttributes: [
		{ 'aria-label': `Min ${props.title}` },
		{ 'aria-label': `Max ${props.title}` }
	]
}));

function tooltipFormatter (value) {
	const valueAsTime = props.uniqueTimes[Number(value).toFixed(0)];
	return dateFormat(new Date(valueAsTime * 1000), 'd MMM');
}

const uniqueTimesOffset = computed(() => {
	return resultQueryStore.state.response.uniqueTimesOffset[props.tripIndex][props.type];
});

const sliderValues = computed({
	get: () => {
		const index = filtersStore.state[props.propertyKey]?.findIndex(item => item.tripIndex === props.tripIndex);
		if (index !== -1) {
			const { start, end } = filtersStore.state[props.propertyKey][index];
			const startIndex = uniqueTimesOffset.value.indexOf(start);
			const endIndex = uniqueTimesOffset.value.indexOf(end);
			return [startIndex, endIndex];
		} else {
			return [0, uniqueTimesOffset.value.length - 1];
		}
	},
	set: (value) => {
		sliderUpdateEvent(value);
		updateFilter();
	}
});

function getFlightTimeText (time) {
	let d = Number(time);

	// calculate (and subtract) whole days
	const days = Math.floor(d / 86400);
	d -= days * 86400;

	// calculate (and subtract) whole hours
	const hours = Math.floor(d / 3600) % 24;
	d -= hours * 3600;

	// calculate whole minutes
	const minutes = Math.floor(d / 60) % 60;

	return addZero(hours) + ':' + addZero(minutes);
}

function handleButtonClick (sliderValues) {
	sliderUpdateEvent(sliderValues);
	updateFilter();
}

function sliderUpdateEvent (sliderValues) {
	const [start, end] = sliderValues;
	sliderHeaderText.value = `${getFlightTimeText(resultQueryStore.state.response.uniqueTimesOffset[props.tripIndex][props.type][start])} - ${getFlightTimeText(resultQueryStore.state.response.uniqueTimesOffset[props.tripIndex][props.type][end])}`;
	filter.value = {
		tripIndex: props.tripIndex,
		start: props.uniqueTimes[start],
		end: props.uniqueTimes[end]
	};
}

function updateFilter () {
	const { propertyKey, tripIndex, type, uniqueTimes } = props;
	const { start, end } = filter.value;
	const startIdx = uniqueTimes.indexOf(start);
	const endIdx = uniqueTimes.indexOf(end);
	const isFullRange = startIdx === 0 && endIdx === uniqueTimes.length - 1;

	const offsets = resultQueryStore.state.response.uniqueTimesOffset[tripIndex][type];
	const newFilter = {
		tripIndex,
		start: offsets[startIdx],
		end: offsets[endIdx]
	};

	const filters = filtersStore.state[propertyKey] || [];
	const copy = [...filters];
	const existingIndex = copy.findIndex(item => item.tripIndex === tripIndex);

	if (isFullRange && existingIndex !== -1) {
		copy.splice(existingIndex, 1);
	} else if (existingIndex !== -1) {
		copy[existingIndex] = newFilter;
	} else {
		copy.push(newFilter);
	}

	filtersStore.set({ key: propertyKey, value: copy });
}


</script>
